import * as React from "react"
import { useParams } from "react-router-dom"

import { AxiosError } from "axios"

import HeadingContainer from "../../../../components/HeadingContainer"
import googleAnalytics from "../../../../lib/googleAnalytics"
import { redirectToCheckout, redirectToPortal } from "../../../../lib/stripe"
import { useAppDispatch, useAppSelector } from "../../../../redux"
import { addSnackbar } from "../../../../redux/snackbars"
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  LoadingButton,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@podcastsoundboard/ps-lib/ui"
import WorkspacePremiumIcon from "@podcastsoundboard/ps-lib/ui/icons/WorkspacePremium"
import CheckIcon from "@podcastsoundboard/ps-lib/ui/icons/Check"
import ShoppingCartIcon from "@podcastsoundboard/ps-lib/ui/icons/ShoppingCart"

export default function AccountSubscriptions(): JSX.Element {
  const dispatch = useAppDispatch()
  const currentUserProfile = useAppSelector((state) => state.currentUserProfile)
  const darkModeEnabled = useAppSelector((state) => state.darkModeEnabled)
  const { status } = useParams()

  const [submitting, setSubmitting] = React.useState(false)
  const [annualModeEnabled, setAnnualModeEnabled] = React.useState(true)
  const [infoMessage, setInfoMessage] = React.useState("")
  const [couponCode, setCouponCode] = React.useState("")

  React.useEffect(() => {
    const storedCoupon = localStorage.getItem("couponCode")
    if (storedCoupon) {
      setCouponCode(storedCoupon)
    }
  }, [])

  const handleCheckoutClick = React.useCallback(
    async (sku: string) => {
      setSubmitting(true)
      try {
        await redirectToCheckout({ sku, couponCode })
      } catch (_err) {
        const err = _err as AxiosError
        setSubmitting(false)
        console.error("Error creating checkout session", err)
        const text =
          (err.response?.data as { error?: string } | undefined)?.error ||
          "Failed to create checkout session."
        dispatch(addSnackbar({ text }))
      }
    },
    [couponCode, dispatch],
  )

  const handlePortalClick = async () => {
    setSubmitting(true)
    try {
      await redirectToPortal()
    } catch (_err) {
      const err = _err as AxiosError
      setSubmitting(false)
      console.error("Error creating checkout session", err)
      const text =
        (err.response?.data as { error?: string } | undefined)?.error ||
        "Failed to create portal session."
      dispatch(addSnackbar({ text }))
    }
  }

  const hasActiveStripeSubscription =
    currentUserProfile && currentUserProfile.hasActiveStripeSubscription

  React.useEffect(() => {
    if (status === "success") {
      googleAnalytics.event({
        category: "user",
        action: "subscribed",
        value: 4.95,
      })

      setInfoMessage("Thanks for subscribing!")
    }
  }, [status])

  return (
    <>
      <HeadingContainer>
        <Typography component="h1" variant="h6" color="textPrimary" noWrap>
          Subscription
        </Typography>

        <Box display="flex" alignItems="center" justifyContent="center">
          <img
            alt="Powered by Stripe"
            src="/img/stripeblurple.svg"
            style={{ height: "2rem" }}
          />
        </Box>
      </HeadingContainer>

      {infoMessage && (
        <Stack className="w-full" spacing={2}>
          <Alert severity="success" className="rounded-none">
            {infoMessage}
          </Alert>
        </Stack>
      )}

      <Container maxWidth="sm" className="pt-8">
        {!hasActiveStripeSubscription && !submitting && (
          <Paper className="p-8 pb-4" elevation={4}>
            <Typography variant="h2" fontSize="2rem" fontWeight="bold">
              Access the fully-online platform
            </Typography>
            <List className="w-full pr-4 my-4">
              <ListItem className="px-0">
                <ListItemAvatar>
                  <Avatar style={{ backgroundColor: "rgba(0,0,0,0)" }}>
                    <CheckIcon
                      className="text-success"
                      style={{ fontSize: "2rem" }}
                    />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  className="text-text-primary mb-4"
                  primary="Unlimited sounds and soundboards"
                  secondary="Enjoy the full use of Podcast Soundboard with limits removed (free accounts are limited to 3 sounds per soundboard)."
                />
              </ListItem>
              <ListItem className="px-0">
                <ListItemAvatar>
                  <Avatar style={{ backgroundColor: "rgba(0,0,0,0)" }}>
                    <img
                      style={{ height: "1.5rem", marginLeft: "-0.25rem" }}
                      alt="Windows"
                      src={
                        darkModeEnabled
                          ? "/img/windows-blue.svg"
                          : "/img/windows-blue.svg"
                      }
                    />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  className="text-text-primary"
                  primary="Windows license now included"
                  secondary="Take your soundboards offline with the desktop app. Generates a Windows license key that is active for the duration of your subscription."
                />
              </ListItem>
              <ListItem className="px-0">
                <ListItemAvatar>
                  <Avatar style={{ backgroundColor: "rgba(0,0,0,0)" }}>
                    <WorkspacePremiumIcon
                      className="text-info"
                      style={{ fontSize: "2rem" }}
                    />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  className="text-text-primary"
                  primary="30-day money-back guarantee"
                  secondary={
                    <>
                      If you try the app and it isn't for you, just reach out at{" "}
                      <Link href="/app/contact" color="primary">
                        https://podcastsoundboard.com/app/contact
                      </Link>
                      .
                    </>
                  }
                />
              </ListItem>
            </List>

            <Box className="mb-4">
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                className="mb-4"
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  gap="0.25rem"
                >
                  <Typography
                    variant="subtitle2"
                    color={annualModeEnabled ? "" : "primary"}
                  >
                    Monthly
                  </Typography>
                  <Switch
                    checked={annualModeEnabled}
                    onChange={() => setAnnualModeEnabled(!annualModeEnabled)}
                  />
                  <Typography
                    variant="subtitle2"
                    color={annualModeEnabled ? "primary" : ""}
                  >
                    Yearly
                  </Typography>
                </Stack>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  className="p-2"
                >
                  {annualModeEnabled ? "$35.40 per year" : "$71.40 per year"}
                </Typography>
              </Stack>

              <Button
                variant="contained"
                startIcon={<ShoppingCartIcon />}
                fullWidth
                onClick={() =>
                  handleCheckoutClick(annualModeEnabled ? "yearly" : "monthly")
                }
              >
                {annualModeEnabled ? "$2.95 per month" : "$4.95 per month"}
              </Button>

              <Stack direction="row" justifyContent="center"></Stack>
            </Box>
          </Paper>
        )}

        {hasActiveStripeSubscription && (
          <Paper elevation={4} className="p-8">
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography
                component="h1"
                variant="h6"
                color="text.secondary"
                textAlign="center"
                className="grow mb-8"
              >
                Manage your subscription by launching your Stripe customer
                portal.
              </Typography>

              <LoadingButton
                color="primary"
                variant="contained"
                loading={submitting}
                onClick={handlePortalClick}
                style={{ backgroundColor: "#635bff" }}
              >
                Launch Portal
              </LoadingButton>
            </Box>
          </Paper>
        )}

        <Box className="mt-12">
          {!hasActiveStripeSubscription && submitting && (
            <Box className="flex justify-center mb-12">
              <CircularProgress className="text-[#635bff]" />
            </Box>
          )}
        </Box>
      </Container>
    </>
  )
}
